import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const EMAIL = 'epicureanexusceo@gmail.com';
const PHONE = '+91 91675 18069';
const PHONE_TEL = '+919167518069';
const WA_LINK = 'https://wa.me/919167518069';

// Shared spring: matches WhyEpicurea / FAQ / EngagementProcess on this page.
const SPRING = { type: 'spring', stiffness: 80, damping: 19, mass: 0.9 };
const SAGE = '#9CC6AE';

export const ContactCTA = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-120px' });
  const reduced = useReducedMotion();

  // Build the reveal — sequential, one element settling at a time.
  const reveal = (delay) => ({
    initial: reduced ? false : { opacity: 0, y: 22 },
    animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    transition: reduced ? { duration: 0.45 } : { ...SPRING, delay },
  });

  return (
    <section
      id="contact"
      data-testid="landing-contact"
      className="relative overflow-hidden"
      style={{ backgroundColor: '#0F0B0A' }}
    >
      {/* Paper grain — same recipe as FAQ / ImpactNumbers. NOT a glow. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='2' seed='17' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Very faint asymmetric sage wash — confined to the lower-left quadrant,
          intentionally offset from the headline so it isn't a "halo behind the
          words". This is purely an atmospheric tint, not a centred glow. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 18% 110%, rgba(156, 198, 174, 0.045) 0%, rgba(156, 198, 174, 0.018) 40%, rgba(15, 11, 10, 0) 70%)',
        }}
      />

      <div ref={ref} className="relative mx-auto max-w-6xl px-6 md:px-10">
        {/* Top hairline + section index — editorial chapter break */}
        <motion.div {...reveal(0)} className="pt-24 md:pt-36 flex items-center gap-4">
          <span className="block h-px w-12 bg-[#C9A66B]/70" />
          <span className="text-[10px] md:text-xs uppercase tracking-[0.34em] text-[#E8D29A]/85 font-semibold">
            Book a Diagnostic
          </span>
          <span className="hidden md:inline-block text-white/30 text-[10px] tracking-[0.24em] uppercase ml-auto">
            Chapter · Close
          </span>
        </motion.div>

        {/* Asymmetric two-column split: headline on the left, action on the
            right with an intentional downward offset so the baselines don't
            mirror each other.  Mobile collapses to a single column. */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-y-16 md:gap-x-10 pb-28 md:pb-40">
          {/* LEFT — editorial headline + body */}
          <div className="col-span-12 md:col-span-7">
            <motion.h2
              {...reveal(reduced ? 0 : 0.1)}
              className="text-white font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.06]"
              style={{ fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif", letterSpacing: '-0.018em' }}
            >
              Stop guessing where your margin went.
              <span
                className="block mt-2 md:mt-3 font-medium"
                style={{ color: SAGE, fontWeight: 500 }}
              >
                Run your restaurant like it has a CFO.
              </span>
            </motion.h2>

            <motion.p
              {...reveal(reduced ? 0 : 0.22)}
              className="mt-10 md:mt-12 text-white/60 text-base md:text-lg leading-[1.75] max-w-[34rem]"
              style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontWeight: 400 }}
            >
              A 30-minute discovery call. We&apos;ll listen, ask the questions that surface where the numbers are hiding a story, and tell you whether a Diagnostic engagement makes sense for your operation.
            </motion.p>

            {/* Hairline + footing — Mumbai stamp */}
            <motion.div
              {...reveal(reduced ? 0 : 0.34)}
              className="mt-12 flex items-center gap-4"
            >
              <span className="block h-px w-8 bg-white/25" />
              <span
                className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-medium"
                style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}
              >
                Mumbai · Reply within 1 working day
              </span>
            </motion.div>
          </div>

          {/* RIGHT — action column. Offset down by mt-2/md:mt-24 so the
              baselines deliberately misalign (editorial, not symmetric). */}
          <div className="col-span-12 md:col-span-5 md:mt-24 md:pl-8">
            <motion.div {...reveal(reduced ? 0 : 0.36)} className="relative">
              {/* Confident, squared primary action — one CTA, weighty hover.
                  The label fill animates from left → right on hover; the
                  arrow shifts up-right. Minimally rounded (4px), not a pill. */}
              <a
                href={`mailto:${EMAIL}?subject=Epicurea%20Nexus%20-%20Discovery%20Call%20Request`}
                data-testid="contact-email"
                className="contact-primary group relative inline-flex w-full items-center justify-between gap-6 px-7 py-6 md:px-8 md:py-7 border border-white/20 hover:border-[#9CC6AE]/0 overflow-hidden transition-colors duration-200"
                style={{ borderRadius: '4px' }}
              >
                {/* sage fill that swipes in on hover */}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[600ms] ease-[cubic-bezier(0.16,0.84,0.32,1)]"
                  style={{ backgroundColor: SAGE }}
                />
                <span className="relative font-display text-white text-2xl md:text-[1.7rem] font-semibold tracking-tight leading-none group-hover:text-[#0F0B0A] transition-colors duration-200">
                  Book a Diagnostic
                </span>
                <span className="relative inline-flex items-center justify-center">
                  <ArrowUpRight
                    className="w-5 h-5 text-white/70 group-hover:text-[#0F0B0A] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300"
                  />
                </span>
              </a>

              {/* Small caption sitting under the button — the email address as
                  understated metadata, not a competing action. */}
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-[0.26em] text-white/35 font-medium">
                  Writes to
                </span>
                <span className="text-white/55 text-xs truncate" data-testid="contact-email-address">
                  {EMAIL}
                </span>
              </div>

              {/* Secondary actions — understated editorial underlined links.
                  Two of them, vertically stacked, separated by a hairline. */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/35 font-medium mb-5">
                  Or speak directly
                </div>
                <div className="space-y-4">
                  <a
                    href={`tel:${PHONE_TEL}`}
                    data-testid="contact-phone"
                    className="group inline-flex items-baseline gap-3 text-white/75 hover:text-white transition-colors"
                  >
                    <span className="text-[10px] uppercase tracking-[0.26em] text-white/40 group-hover:text-[#9CC6AE] transition-colors">
                      Call
                    </span>
                    <span
                      className="text-base md:text-lg underline decoration-white/25 decoration-[1.5px] underline-offset-[6px] group-hover:decoration-[#9CC6AE] group-hover:underline-offset-[8px] transition-all duration-300"
                      style={{ fontFamily: "'Roboto', system-ui, sans-serif", fontWeight: 500, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}
                    >
                      {PHONE}
                    </span>
                  </a>
                  <div className="block">
                    <a
                      href={WA_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp"
                      className="group inline-flex items-baseline gap-3 text-white/75 hover:text-white transition-colors"
                    >
                      <span className="text-[10px] uppercase tracking-[0.26em] text-white/40 group-hover:text-[#9CC6AE] transition-colors">
                        WhatsApp
                      </span>
                      <span
                        className="text-base md:text-lg underline decoration-white/25 decoration-[1.5px] underline-offset-[6px] group-hover:decoration-[#9CC6AE] group-hover:underline-offset-[8px] transition-all duration-300"
                        style={{ fontFamily: "'Roboto', system-ui, sans-serif", fontWeight: 500, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}
                      >
                        {PHONE}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer className="relative border-t border-white/10 py-16 md:py-20" data-testid="landing-footer" style={{ backgroundColor: '#0F0B0A' }}>
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="font-display text-white font-semibold text-base leading-none tracking-tight border border-[#C9A66B]/45 rounded-full w-9 h-9 flex items-center justify-center">
              <span className="italic">E·N</span>
            </span>
            <span className="font-display text-white font-semibold tracking-tight text-lg">Epicurea Nexus</span>
          </div>
          <p className="text-white/50 text-sm leading-relaxed max-w-md">
            A finance practice for the hospitality industry. Weekly briefings and monthly reads written over deterministic numbers — with the reconciliation, recovery and dispute workflow running quietly in the background.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-white/35 text-[10px] uppercase tracking-[0.28em] font-semibold mb-4">Contact</div>
          <div className="space-y-3 text-sm">
            <a href={`mailto:${EMAIL}`} className="block text-white/65 hover:text-white transition-colors" data-testid="footer-email">{EMAIL}</a>
            <a href={`tel:${PHONE_TEL}`} className="block text-white/65 hover:text-white transition-colors" data-testid="footer-phone">{PHONE}</a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="block text-white/65 hover:text-white transition-colors" data-testid="footer-whatsapp">WhatsApp</a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-white/35 text-[10px] uppercase tracking-[0.28em] font-semibold mb-4">Practice</div>
          <div className="space-y-3 text-sm">
            <a href="#approach" className="block text-white/65 hover:text-white transition-colors">Reports</a>
            <a href="#pricing" className="block text-white/65 hover:text-white transition-colors">Engagement</a>
            <a href="#faq" className="block text-white/65 hover:text-white transition-colors">FAQ</a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-white/35 text-[10px] uppercase tracking-[0.28em] font-semibold mb-4">Legal</div>
          <div className="space-y-3 text-sm">
            <a href="#" className="block text-white/65 hover:text-white transition-colors">Privacy</a>
            <a href="#" className="block text-white/65 hover:text-white transition-colors">Terms</a>
            <a href="/login" className="block text-white/65 hover:text-white transition-colors" data-testid="footer-login">Client Login</a>
          </div>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/35">
        <div>© {new Date().getFullYear()} Epicurea Nexus. All rights reserved.</div>
        <div className="text-white/30">Proprietary platform · Not licensed to third parties.</div>
      </div>
    </div>
  </footer>
);

export default ContactCTA;
